import React, { useState, useEffect, useMemo, Fragment } from "react";
import { Plus } from "react-feather";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Box,
  Typography,
} from "@mui/material";
import { DropdownItem } from "reactstrap";
import CustomSearchFilter from "../../Common/Component/CustomSearchFilter";
import ActionOptions from "../../Common/Component/ActionOptions";
import StatusBadge from "../../Common/Component/StatusBadge";
import { showAlert } from "../../../Services/Shared.service";
import { getRoutesByCity, deleteRoute } from "../../../Services/ServiceCity.service";
import TariffModal from "../../../CommonElements/TariffModal/TariffModal";
import SuburbForm from "./SuburbForm";
import DeleteConfirmDialog from "../../Common/Component/DeleteConfirmDialog";
import { Spinner } from "../../../AbstractElements";

const SuburbTab = ({ cityId }) => {
  const [routesData, setRoutesData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchData, setSearchData] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Modal and Dialog States
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRoute, setEditingRoute] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [routeToDelete, setRouteToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchRoutes = () => {
    setIsLoading(true);
    getRoutesByCity(cityId)
      .then((res) => {
        if (res?.data?.data) {
          setRoutesData(res.data.data);
        } else {
          setRoutesData([]);
        }
      })
      .catch((err) => {
        const errorMsg = err.response?.data?.message || "Failed to fetch routes";
        showAlert("error", errorMsg);
        setRoutesData([]);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleSaveRoute = (savedRoute, isEdit) => {
    setRoutesData((prev) => {
      if (isEdit) {
        return prev.map((item) =>
          item.otherChargesId === savedRoute.otherChargesId ? savedRoute : item
        );
      } else {
        return [savedRoute, ...prev];
      }
    });
  };

  useEffect(() => {
    if (cityId) {
      fetchRoutes();
    }
  }, [cityId]);

  const filteredData = useMemo(() => {
    if (!searchData) return routesData;
    const query = searchData.toLowerCase();
    return routesData.filter(
      (item) =>
        (item.fromSuburbName && item.fromSuburbName.toLowerCase().includes(query)) ||
        (item.toSuburbName && item.toSuburbName.toLowerCase().includes(query)) ||
        (item.charge && item.charge.toString().includes(query))
    );
  }, [routesData, searchData]);

  useEffect(() => {
    setPage(0);
  }, [searchData]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleAddClick = () => {
    setEditingRoute(null);
    setIsModalOpen(true);
  };

  const handleEditClick = (row) => {
    setEditingRoute(row);
    setIsModalOpen(true);
  };

  const handleDeleteClick = (row) => {
    setRouteToDelete(row);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (!routeToDelete) return;
    setIsDeleting(true);
    const actionType = routeToDelete.isActive ? "delete" : "recover";
    deleteRoute(cityId, routeToDelete.otherChargesId)
      .then(() => {
        showAlert("success", `Route ${actionType === "delete" ? "deleted" : "recovered"} successfully`);
        setDeleteDialogOpen(false);
        setRoutesData((prev) =>
          prev.map((item) =>
            item.otherChargesId === routeToDelete.otherChargesId
              ? { ...item, isActive: !item.isActive }
              : item
          )
        );
        setRouteToDelete(null);
      })
      .catch((err) => {
        const errorMsg = err.response?.data?.message || `Failed to ${actionType} route`;
        showAlert("error", errorMsg);
      })
      .finally(() => {
        setIsDeleting(false);
      });
  };

  const paginatedData = useMemo(() => {
    return filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [filteredData, page, rowsPerPage]);

  return (
    <Box sx={{ marginTop: "30px" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
          gap: "15px",
          flexWrap: "wrap",
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: "700", color: "#313131" }}>
          Suburb Routes
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center", gap: "15px" }}>
          <Box sx={{ minWidth: "250px" }}>
            <CustomSearchFilter
              searchData={searchData}
              setSearchData={setSearchData}
              placeholder="Search Suburb Routes"
            />
          </Box>
          <Box
            style={{
              color: "rgba(57, 181, 74, 1)",
              border: "1px solid rgba(57, 181, 74, 1)",
              borderRadius: "10px",
              backgroundColor: "rgba(225, 244, 228, 1)",
              height: "39px",
              minWidth: "140px",
              fontSize: "14px",
              padding: "0 20px",
              textTransform: "none",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              cursor: "pointer",
              fontWeight: "600",
              whiteSpace: "nowrap",
            }}
            onClick={handleAddClick}
          >
            <Plus size={18} />
            Add Suburb Route
          </Box>
        </Box>
      </Box>

      {isLoading ? (
        <div className="loader-box" style={{ height: "20vh", display: "flex", justifyContent: "center", alignItems: "center" }}>
          <Spinner attrSpinner={{ className: "loader-3" }} />
        </div>
      ) : (
        <>
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              backgroundColor: "transparent",
              boxShadow: "0 4px 10px 1px rgba(8, 8, 8, 0.15)",
              borderRadius: "10px",
              overflow: "hidden",
            }}
          >
            <Table stickyHeader>
              <TableHead>
                <TableRow
                  sx={{
                    "& th": {
                      color: "#626262",
                      fontWeight: "bold",
                      fontSize: "14px",
                      backgroundColor: "#E1F4E4",
                      borderBottom: "none",
                      whiteSpace: "nowrap",
                    },
                  }}
                >
                  <TableCell>From Suburb</TableCell>
                  <TableCell>To Suburb</TableCell>
                  <TableCell>Route Charge</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center" sx={{ width: "80px" }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                      No Suburb Routes found
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedData.map((row) => (
                    <TableRow
                      key={row.otherChargesId}
                      sx={{
                        "& td": {
                          borderBottom: "none",
                          fontSize: "14px",
                          color: "#313131",
                        },
                      }}
                    >
                      <TableCell style={{ fontWeight: "600" }}>{row.fromSuburbName}</TableCell>
                      <TableCell style={{ fontWeight: "600" }}>{row.toSuburbName}</TableCell>
                      <TableCell>₹{parseFloat(row.charge || 0).toFixed(2)}</TableCell>
                      <TableCell>
                        <StatusBadge isDeleted={!row.isActive} onClick={() => handleDeleteClick(row)} />
                      </TableCell>
                      <TableCell align="center">
                        <ActionOptions key={row.otherChargesId}>
                          <Fragment>
                            <DropdownItem
                              className="border-0"
                              onClick={() => handleDeleteClick(row)}
                            >
                              {row.isActive ? (
                                <Fragment>
                                  <i className="fa fa-trash-o" /> Delete
                                </Fragment>
                              ) : (
                                <Fragment>
                                  <i className="fa fa-recycle" /> Recover
                                </Fragment>
                              )}
                            </DropdownItem>
                            <DropdownItem
                              className="border-0"
                              onClick={() => handleEditClick(row)}
                            >
                              <i className="fa fa-pencil" /> Edit
                            </DropdownItem>
                          </Fragment>
                        </ActionOptions>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={filteredData.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
            sx={{
              "& .MuiTablePagination-toolbar": {
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: 2,
                textAlign: "center",
                overflowX: "hidden",
              },
              "& .MuiTablePagination-select": {
                backgroundColor: "rgba(255, 255, 255, 1)",
                borderRadius: "6px",
                border: "1px solid #D9D9D9",
              },
              "& .MuiTablePagination-actions button": {
                color: "rgba(112, 112, 112, 1)",
                transform: "scale(1.3)",
              },
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                color: "rgba(98, 98, 98, 1)",
                fontSize: "14px",
                textAlign: "center",
                m: "auto",
                letterSpacing: "0.02vw",
              },
            }}
          />
        </>
      )}

      {/* Suburb Form Modal */}
      <TariffModal
        isOpen={isModalOpen}
        size="lg"
        centered
        toggler={() => setIsModalOpen(false)}
        title={editingRoute ? "Edit Route" : "Add Suburb Route"}
      >
        <SuburbForm
          cityId={cityId}
          editingRoute={editingRoute}
          toggleModal={() => setIsModalOpen(false)}
          onRouteSaved={handleSaveRoute}
          fetchRoutes={fetchRoutes}
        />
      </TariffModal>

      {/* Delete/Recover Confirmation Dialog */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        title={routeToDelete?.isActive ? "Delete Suburb Route" : "Recover Suburb Route"}
        message={`Are you sure you want to ${routeToDelete?.isActive ? "delete" : "recover"} the suburb route between "${routeToDelete?.fromSuburbName}" and "${routeToDelete?.toSuburbName}"?`}
        loading={isDeleting}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteDialogOpen(false)}
      />
    </Box>
  );
};

export default SuburbTab;
